/**
 * `WhatsLeftDrawer` — opens from the readiness ring and from the
 * "N things to fix" line in the header. Explains the ring colour and
 * groups failing checks by severity:
 *
 *   - Blocking  (coral, makes the ring red)
 *   - To improve (amber)
 *   - Not counted (unknown / na with reason — doesn't move the ring)
 *
 * Each row shows the check headline + a muted reason + a "Fix this" link
 * that opens the usual Fix drawer for that check. One line at the top
 * explains the overall ring colour verbatim per spec §I.
 */

import type { ReactNode } from 'react';

import type { ReadinessCheck } from '@sill/types';
import { INFORMATIONAL_CHECK_IDS } from '@sill/types';

import { Drawer } from '../../components/atoms/Drawer.js';
import { useC } from '../../lib/theme.js';
import { monoFont, sansFont, serifFont } from './fonts.js';

export interface WhatsLeftDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly checks: readonly ReadinessCheck[];
  readonly onFix: (check: ReadinessCheck) => void;
}

interface Group {
  readonly key: 'block' | 'warn' | 'uncounted';
  readonly label: string;
  readonly tone: 'coral' | 'amber' | 'neutral';
  readonly checks: readonly ReadinessCheck[];
}

function partition(checks: readonly ReadinessCheck[]): readonly Group[] {
  const block: ReadinessCheck[] = [];
  const warn: ReadinessCheck[] = [];
  const uncounted: ReadinessCheck[] = [];
  for (const c of checks) {
    // ADR-0225 — informational rows (x402) are not something to do.
    if (INFORMATIONAL_CHECK_IDS.has(c.check_id)) continue;
    if (c.state === 'fail' && !c.uncounted) {
      if (c.severity === 'block') block.push(c);
      else warn.push(c);
    } else if (c.state === 'unknown' || c.state === 'na' || c.uncounted) {
      // Only surface rows that merit explanation (have a headline).
      if (c.headline.length > 0) uncounted.push(c);
    }
  }
  return [
    { key: 'block', label: 'Blocking', tone: 'coral', checks: block },
    { key: 'warn', label: 'To improve', tone: 'amber', checks: warn },
    { key: 'uncounted', label: "Not counted", tone: 'neutral', checks: uncounted },
  ];
}

function Dot({ tone }: { tone: 'coral' | 'amber' | 'neutral' }): JSX.Element {
  const C = useC();
  const bg = tone === 'coral' ? C.coral : tone === 'amber' ? C.amber : C.dim;
  return (
    <span
      aria-hidden="true"
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: bg,
        flexShrink: 0,
        marginTop: 7,
      }}
    />
  );
}

function Section({ title, tone, children }: { title: string; tone: 'coral' | 'amber' | 'neutral'; children: ReactNode }): JSX.Element {
  const C = useC();
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: tone === 'coral' ? C.coralFg : tone === 'amber' ? C.amberFg : C.muted,
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </section>
  );
}

function reasonLineFor(check: ReadinessCheck): string {
  if (check.state === 'fail') return check.evidence;
  if (check.state === 'na') return check.evidence.length > 0 ? check.evidence : 'Not applicable on this install.';
  if (check.state === 'unknown') return check.evidence.length > 0 ? check.evidence : 'Not measured yet.';
  if (check.uncounted) return check.evidence.length > 0 ? check.evidence : 'Emerging standard — not counted toward the total.';
  return check.evidence;
}

export function WhatsLeftDrawer({ open, onClose, checks, onFix }: WhatsLeftDrawerProps): JSX.Element {
  const C = useC();
  const groups = partition(checks);

  return (
    <Drawer open={open} onClose={onClose} title="What's left" data-testid="readiness-whats-left-drawer">
      <div style={{ fontFamily: sansFont, color: C.text, padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, textTransform: 'uppercase' }}>
          Readiness · What&apos;s left
        </div>
        <h3 style={{ margin: 0, fontFamily: serifFont, fontWeight: 400, fontSize: 'clamp(22px, 3.4vw, 28px)', color: C.text }}>
          What&apos;s left to fix
        </h3>
        <p
          data-testid="readiness-whats-left-colour-explainer"
          style={{ margin: 0, fontSize: 13.5, color: C.body, lineHeight: 1.55 }}
        >
          Red means at least one check blocks agents. Amber means only improvements are left. Green means nothing is left. Checks that don&apos;t apply to your site, or couldn&apos;t be checked, aren&apos;t counted — that&apos;s why sites show different totals.
        </p>
        {groups.every((g) => g.checks.length === 0) ? (
          <div style={{ fontSize: 13, color: C.muted }}>
            Nothing to fix right now.
          </div>
        ) : (
          groups.filter((g) => g.checks.length > 0).map((g) => (
            <Section key={g.key} title={`${g.label} · ${g.checks.length}`} tone={g.tone}>
              {g.checks.map((c) => (
                <div
                  key={c.check_id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    padding: '12px 14px',
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 3,
                  }}
                >
                  <Dot tone={g.tone} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: C.text }}>{c.headline}</div>
                    {c.evidence.length > 0 ? (
                      <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
                        {reasonLineFor(c)}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => onFix(c)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      color: C.text,
                      fontFamily: monoFont,
                      fontSize: 11,
                      textDecoration: 'underline',
                      textDecorationColor: C.dim,
                      textUnderlineOffset: '3px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    aria-label={`Open ${c.headline}`}
                  >
                    {g.key === 'uncounted' ? 'Details' : 'Fix this'}
                  </button>
                </div>
              ))}
            </Section>
          ))
        )}
      </div>
    </Drawer>
  );
}
